<script setup lang="ts">
import {
  Collection,
  Files,
  Memo,
  PriceTag,
  Avatar,
  Bell,
  Box,
  CircleCheck,
  OfficeBuilding,
  Document,
  Fold,
  Lock,
  Money,
  Odometer,
  Operation,
  Picture,
  Search,
  Setting,
  Switch,
  SwitchButton,
  Tickets,
  User,
  UserFilled,
  Van,
  Wallet,
} from '@element-plus/icons-vue';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { ADMIN_SESSION_CHANGED_EVENT, clearAdminSession, getAdminSession } from './auth/session';
import { ApiClientError, apiRequest } from './http/api-client';
import {
  refreshTodos,
  startTodoPolling,
  stopTodoPolling,
  todoCountForRoute,
  todoState,
} from './services/todo-center';

const route = useRoute();
const router = useRouter();
const collapsed = ref(false);
const mobileNavOpen = ref(false);
const permissions = ref<ReadonlySet<string>>(new Set());
const displayName = ref('');
const pageTitle = computed(() =>
  typeof route.meta.title === 'string' ? route.meta.title : '管理中心',
);
const standalone = computed(() => route.meta.standalone === true);
let principalRequest = 0;

async function loadPrincipal(): Promise<void> {
  const request = ++principalRequest;
  if (!getAdminSession()) {
    displayName.value = '';
    permissions.value = new Set();
    stopTodoPolling();
    todoOpen.value = false;
    return;
  }
  try {
    const principal = await apiRequest<{ displayName: string; permissions: string[] }>('/auth/me');
    if (request !== principalRequest) return;
    displayName.value = principal.displayName;
    permissions.value = new Set(principal.permissions);
    try {
      localStorage.setItem('admin_perms', JSON.stringify(principal.permissions));
    } catch {
      /* 快照失败不阻断 */
    }
    startTodoPolling();
  } catch (cause) {
    if (request !== principalRequest) return;
    displayName.value = '';
    permissions.value = new Set();
    stopTodoPolling();
    todoOpen.value = false;
    if (cause instanceof ApiClientError && cause.code === 'UNAUTHENTICATED') {
      clearAdminSession();
      try {
        localStorage.removeItem('admin_perms');
      } catch {
        /* 忽略 */
      }
      try {
      } catch {
        /* 忽略 */
      }
      if (route.meta.public !== true) {
        await router.replace({ name: 'login', query: { redirect: route.fullPath } });
      }
    }
  }
}

function handleSessionChanged(): void {
  void loadPrincipal();
}

onMounted(() => {
  globalThis.addEventListener(ADMIN_SESSION_CHANGED_EVENT, handleSessionChanged);
  document.addEventListener('click', closeTodoOnOuterClick);
  void loadPrincipal();
});

onBeforeUnmount(() => {
  globalThis.removeEventListener(ADMIN_SESSION_CHANGED_EVENT, handleSessionChanged);
  document.removeEventListener('click', closeTodoOnOuterClick);
  stopTodoPolling();
});

watch(
  () => route.fullPath,
  () => {
    mobileNavOpen.value = false;
    if (route.meta.public !== true) void loadPrincipal();
  },
);

function can(permission: string): boolean {
  return permissions.value.has(permission);
}

function toggleNavigation(): void {
  if (globalThis.matchMedia?.('(max-width: 900px)').matches) {
    mobileNavOpen.value = !mobileNavOpen.value;
    return;
  }
  collapsed.value = !collapsed.value;
}

async function logout(): Promise<void> {
  try {
    if (getAdminSession()) await apiRequest('/auth/logout', { method: 'POST' });
  } finally {
    stopTodoPolling();
    todoOpen.value = false;
    clearAdminSession();
    try {
    } catch {
      /* 忽略 */
    }
    await router.replace('/login');
  }
}

const todoOpen = ref(false);

function toggleTodo(): void {
  todoOpen.value = !todoOpen.value;
  if (todoOpen.value) void refreshTodos();
}

function openTodo(route: string): void {
  todoOpen.value = false;
  void router.push(route);
}

function closeTodoOnOuterClick(): void {
  todoOpen.value = false;
}
</script>

<template>
  <RouterView v-if="standalone" />
  <div
    v-else
    class="app-shell"
    :class="{ 'is-collapsed': collapsed, 'mobile-nav-open': mobileNavOpen }"
  >
    <button
      v-if="mobileNavOpen"
      class="sidebar-scrim"
      type="button"
      aria-label="关闭导航"
      @click="mobileNavOpen = false"
    ></button>
    <aside class="sidebar">
      <div class="brand">
        <div class="brand-mark">聚</div>
        <div class="brand-copy">
          <strong>聚車缘</strong>
          <span>Mobility OS</span>
        </div>
      </div>
      <nav aria-label="主导航">
        <RouterLink to="/" class="nav-item"><Odometer /><em>经营总览</em></RouterLink>
        <span class="nav-caption">客户</span>
        <RouterLink v-if="can('rental_request.read')" to="/rental-requests" class="nav-item"
          ><Tickets /><em>租赁请求</em
          ><i v-if="todoCountForRoute('/rental-requests')" class="nav-todo">{{
            todoCountForRoute('/rental-requests')
          }}</i></RouterLink
        >
        <RouterLink v-if="can('customer.pool.read')" to="/customers" class="nav-item"
          ><User /><em>客户公海</em></RouterLink
        >
        <RouterLink
          v-if="can('customer.scope_verification.read')"
          to="/customers/scope-verification"
          class="nav-item"
          ><Search /><em>归属核实</em></RouterLink
        >
        <RouterLink
          v-if="can('customer.identity.read')"
          to="/customers/identity-verifications"
          class="nav-item"
          ><UserFilled /><em>身份审核</em
          ><i v-if="todoCountForRoute('/customers/identity-verifications')" class="nav-todo">{{
            todoCountForRoute('/customers/identity-verifications')
          }}</i></RouterLink
        >
        <span class="nav-caption">车源</span>
        <RouterLink v-if="can('vehicle.read')" to="/vehicles" class="nav-item"
          ><Van /><em>车辆资产</em></RouterLink
        >
        <RouterLink v-if="can('vehicle.manage')" to="/vehicles/taxonomy" class="nav-item"
          ><PriceTag /><em>品牌分类</em></RouterLink
        >
        <span class="nav-caption">交易</span>
        <RouterLink
          v-if="can('contract.read.self') || can('contract.read.store')"
          to="/contracts/drafts"
          class="nav-item"
          ><Document /><em>合同方案</em><i>草稿</i
          ><i v-if="todoCountForRoute('/contracts/drafts')" class="nav-todo">{{
            todoCountForRoute('/contracts/drafts')
          }}</i></RouterLink
        >
        <RouterLink v-if="can('order.fulfillment.read')" to="/fulfillment" class="nav-item"
          ><Box /><em>订单履约</em></RouterLink
        >
        <RouterLink
          v-if="can('finance.bill.read') || can('finance.bank_transfer.read')"
          to="/finance"
          class="nav-item"
          ><Money /><em>财务中心</em><i>线下</i
          ><i v-if="todoCountForRoute('/finance')" class="nav-todo">{{
            todoCountForRoute('/finance')
          }}</i></RouterLink
        >
        <RouterLink v-if="can('finance.bill.read')" to="/finance/cases" class="nav-item"
          ><Wallet /><em>资金事项</em
          ><i v-if="todoCountForRoute('/finance/cases')" class="nav-todo">{{
            todoCountForRoute('/finance/cases')
          }}</i></RouterLink
        >
        <RouterLink
          v-if="can('finance.deposit.deduct.create')"
          to="/finance/deposit-offset-requests"
          class="nav-item"
          ><Wallet /><em>保证金抵扣申请</em><i>待办</i
          ><i v-if="todoCountForRoute('/finance/deposit-offset-requests')" class="nav-todo">{{
            todoCountForRoute('/finance/deposit-offset-requests')
          }}</i></RouterLink
        >
        <RouterLink
          v-if="can('finance.partner_payable.read')"
          to="/finance/partner-payables"
          class="nav-item"
          ><Wallet /><em>合作方应付</em><i>月付</i></RouterLink
        >
        <span class="nav-caption">内容</span>
        <RouterLink v-if="can('content.homepage.read')" to="/content/pages" class="nav-item"
          ><Files /><em>微页面管理</em></RouterLink
        >
        <RouterLink v-if="can('content.asset.manage')" to="/content/assets" class="nav-item"
          ><Picture /><em>素材库</em></RouterLink
        >
        <RouterLink v-if="can('showcase.read')" to="/content/showcase" class="nav-item"
          ><Picture /><em>交付展示</em></RouterLink
        >
        <span class="nav-caption">组织</span>
        <RouterLink v-if="can('organization.staff.read')" to="/organization/staff" class="nav-item"
          ><UserFilled /><em>员工与权限</em></RouterLink
        >
        <RouterLink v-if="can('organization.store.read')" to="/organization/stores" class="nav-item"
          ><OfficeBuilding /><em>门店管理</em></RouterLink
        >
        <RouterLink
          v-if="can('organization.manager_transfer.read')"
          to="/organization/transfers"
          class="nav-item"
          ><Switch /><em>经理交接</em></RouterLink
        >
        <RouterLink
          v-if="can('contract.party_profile.read')"
          to="/organization/party-profiles"
          class="nav-item"
          ><Avatar /><em>甲方档案</em></RouterLink
        >
        <span class="nav-caption">系统</span>
        <RouterLink v-if="can('integration.config.read')" to="/system/integrations" class="nav-item"
          ><Setting /><em>第三方集成</em></RouterLink
        >
        <RouterLink v-if="can('dict.manage')" to="/system/dicts" class="nav-item"
          ><Collection /><em>业务字典</em></RouterLink
        >
        <RouterLink v-if="can('audit.read')" to="/system/audit-log" class="nav-item"
          ><Memo /><em>审计日志</em></RouterLink
        >
        <RouterLink
          v-if="can('security.password_reset.issue')"
          to="/system/password-reset"
          class="nav-item"
          ><Lock /><em>密码重置</em></RouterLink
        >
        <RouterLink
          v-if="can('release.candidate.read')"
          to="/system/release-signoffs"
          class="nav-item"
          ><Operation /><em>Release发布</em><i>单人</i></RouterLink
        >
        <RouterLink to="/system/readiness" class="nav-item"
          ><CircleCheck /><em>上线准备</em></RouterLink
        >
      </nav>
      <div class="sidebar-foot">R1 · Release Candidate</div>
    </aside>

    <main class="workspace">
      <header class="topbar">
        <button
          class="collapse-button"
          type="button"
          aria-label="切换侧边栏"
          @click="toggleNavigation"
        >
          <Fold />
        </button>
        <div>
          <p>聚車缘运营管理</p>
          <h1>{{ pageTitle }}</h1>
        </div>
        <div class="topbar-actions">
          <span class="environment"><i></i>管理端</span>
          <span v-if="displayName" class="environment">{{ displayName }}</span>
          <div class="todo-bell-wrap">
            <button
              class="avatar todo-bell"
              type="button"
              title="待办事项"
              aria-label="待办事项"
              @click.stop="toggleTodo"
            >
              <Bell />
              <i v-if="todoState.total > 0" class="todo-badge">{{
                todoState.total > 99 ? '99+' : todoState.total
              }}</i>
            </button>
            <div v-if="todoOpen" class="todo-panel" @click.stop>
              <header>
                <strong>待办事项</strong>
                <small>{{ todoState.total }} 项待处理</small>
              </header>
              <p v-if="!todoState.items.length" class="todo-empty">暂无待办，一切就绪</p>
              <button
                v-for="item in todoState.items"
                :key="item.key"
                type="button"
                class="todo-row"
                @click="openTodo(item.route)"
              >
                <span>{{ item.label }}</span>
                <strong>{{ item.count > 99 ? '99+' : item.count }}</strong>
              </button>
            </div>
          </div>
          <button
            class="avatar"
            type="button"
            title="退出登录"
            aria-label="退出登录"
            @click="logout"
          >
            <SwitchButton />
          </button>
        </div>
      </header>
      <section class="page-content"><RouterView /></section>
    </main>
  </div>
</template>
